import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import Button, { ButtonType } from "../../components/Assets/Button";
import { Info } from "lucide-react";
import TaskDetailsModal from "./TaskDetailsModal";
import dayjs from "dayjs";
import { ITaskData, ITaskRemark } from "../../models/TaskData.model";
import Select, { SingleValue } from "react-select";
import { getPendingTasks } from "../../store/slices/TaskSlice";

interface PendingTaskProps {
  getRemarks: (taskId: string) => void;
  setOpenDoneTaskId: (taskId: string | null) => void;
  setIsTaskOpen: (isOpen: boolean) => void;
  isTaskOpen: boolean;
  openDoneTaskId: string | null;
  // Add required modal props
  remarks: ITaskRemark[];
  newFollowUpDate: dayjs.Dayjs | null;
  newFollowUpTime: dayjs.Dayjs | null;
  setNewFollowUpDate: (date: dayjs.Dayjs | null) => void;
  setNewFollowUpTime: (time: dayjs.Dayjs | null) => void;
  completeChecked: boolean;
  setCompleteChecked: (checked: boolean) => void;
  handleAddRemarkOrAction: (taskId: string) => void;
  taskRemarks: { [key: string]: string };
  handleRemarkChange: (taskId: string, value: string) => void;
  selectedUserId: string | null;
  setSelectedUserId: (id: string | null) => void;
}

const PendingTask: React.FC<PendingTaskProps> = ({
  getRemarks,
  setOpenDoneTaskId,
  setIsTaskOpen,
  isTaskOpen,
  openDoneTaskId,
  remarks,
  newFollowUpDate,
  newFollowUpTime,
  setNewFollowUpDate,
  setNewFollowUpTime,
  completeChecked,
  setCompleteChecked,
  handleAddRemarkOrAction,
  taskRemarks,
  handleRemarkChange,
  selectedUserId,
  setSelectedUserId,
}) => {
  const dispatch: RootDispatch = useDispatch();

  const pendingTasks = useSelector(
    (state: RootState) => state.task.pendingTasks
  );
  const { allUsersNames } = useSelector((state: RootState) => state.users);
  const selectedTask = pendingTasks.find((task) => task._id === openDoneTaskId);


  const userOptions = allUsersNames.map((user) => ({
    label: user.username,
    value: user._id,
  }));

  type UserOption = {
    label: string;
    value: string;
  };

  useEffect(() => {
    dispatch(getPendingTasks(selectedUserId));
  }, [dispatch, selectedUserId]);

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
      <h2 className="text-lg font-semibold text-slate-800 flex items-center mb-4">
        <span className="w-1 h-6 bg-amber-500 mr-3 rounded-full" />
        Pending Tasks
      </h2>
      <div className="mb-4 w-full md:w-80 max-w-sm">
        <Select
          options={userOptions}
          value={
            userOptions.find((opt) => opt.value === selectedUserId) || null
          }
          onChange={(option: SingleValue<UserOption>) =>
            setSelectedUserId(option?.value ?? null)
          }
          placeholder="Select Assignee..."
          isClearable
        />
      </div>{" "}
      {pendingTasks.length === 0 ? (
        <p className="text-gray-600">No pending tasks</p>
      ) : (
        <ul className="space-y-4">
          {pendingTasks.map((task: ITaskData, index: number) => (
            <li
              key={task._id || index}
              className="p-4 bg-white rounded-lg shadow-md border-l-4 border-amber-500"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <span className="text-lg font-semibold text-amber-500">
                    {index + 1}.
                  </span>
                  <div className="flex flex-col">
                    <h3 className="text-md font-medium text-gray-800">
                      {task.taskSummary}
                    </h3>
                    <p className="text-xs text-gray-600">
                      Follow-up:{" "}
                      {task.followUp
                        ? new Date(task.followUp.toString()).toLocaleString()
                        : "Not Scheduled"}
                    </p>
                    <p className="text-xs text-blue-600">
                      Lead: {task.lead?.name ?? "N/A"}
                    </p>
                    <p className="text-xs text-blue-600">
                      Assignee: {(task.assignee as any)?.username || "N/A"}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    getRemarks(task._id as string);
                    setOpenDoneTaskId(task._id as string);
                    setIsTaskOpen(true);
                  }}
                  type={ButtonType.Submit}
                  className="flex items-center gap-2 bg-transparent md:bg-navyBlue-primary"
                >
                  <span className="hidden md:inline">Details</span>
                  <Info className="w-5 h-5 text-green-600 md:hidden" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {/* Render modal outside of list */}
      {isTaskOpen && selectedTask && (
        <TaskDetailsModal
          isOpen={isTaskOpen}
          onClose={() => {
            setIsTaskOpen(false);
            setOpenDoneTaskId(null);
          }}
          task={selectedTask}
          remarks={remarks}
          newFollowUpDate={newFollowUpDate}
          newFollowUpTime={newFollowUpTime}
          setNewFollowUpDate={setNewFollowUpDate}
          setNewFollowUpTime={setNewFollowUpTime}
          completeChecked={completeChecked}
          setCompleteChecked={setCompleteChecked}
          onAddRemark={() => handleAddRemarkOrAction(openDoneTaskId!)}
          taskRemarks={taskRemarks[openDoneTaskId!] || ""}
          onRemarkChange={(value) => handleRemarkChange(openDoneTaskId!, value)}
        />
      )}
    </div>
  );
};

export default PendingTask;
